// Gradixia — the theme: light, dark, or the system's choice.
//
// Three states, on purpose. "system" stamps nothing on <html> and lets the
// stylesheet's media query decide; "light" and "dark" stamp data-theme and
// are remembered in localStorage under one key, which index.html reads
// before the first paint so a dark reader never sees a white flash. The
// toggle cycles system → dark → light → system, and always says what it did.

import { useCallback, useEffect, useState } from "react";

export type Theme = "system" | "light" | "dark";
export const THEME_KEY = "gradixia-theme";

const read = (): Theme => {
  if (typeof window === "undefined") return "system";
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    return v === "dark" || v === "light" ? v : "system";
  } catch { return "system"; }
};

/** Stamp (or clear) the attribute and remember the choice. Exported so a
    test can drive it without a component. */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
  try {
    if (theme === "system") window.localStorage.removeItem(THEME_KEY);
    else window.localStorage.setItem(THEME_KEY, theme);
  } catch { /* private mode: the choice lasts the session */ }
}

/** What the reader actually sees right now, resolving "system". */
export function effectiveTheme(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function useTheme(): { theme: Theme; effective: "light" | "dark"; cycle: () => void; set: (t: Theme) => void } {
  const [theme, setTheme] = useState<Theme>(read);
  const [effective, setEffective] = useState<"light" | "dark">(() => effectiveTheme(read()));

  useEffect(() => {
    applyTheme(theme);
    setEffective(effectiveTheme(theme));
    if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setEffective(mq.matches ? "dark" : "light");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const cycle = useCallback(() => {
    setTheme((t) => (t === "system" ? "dark" : t === "dark" ? "light" : "system"));
  }, []);

  return { theme, effective, cycle, set: setTheme };
}
